/**
 * Typed client for /shareholder-portal/* endpoints — the read-only
 * investor-facing surface of the cap table system. All 7 endpoints
 * require a JWT with role='shareholder' (or 'admin' for testing).
 *
 * The data shapes mirror the legacy publisher dashboard's shareholder
 * portal (where these endpoints have served from since the cap-table
 * feature shipped). investors.nurev.io is a focused front-end on the
 * same backend — no model or controller changes needed.
 */
import { client } from './client';

/**
 * Real shape from `GET /shareholder-portal/my-shares` — confirmed
 * 2026-05-28 against prod. The controller flattens shareholder + config
 * fields into one object via `utils.handleSuccess(res, 200, {...})`, so
 * after our `unwrap()` strips the `{success, data}` envelope this is
 * what the SPA gets. (Earlier shape `{shareholder:{...}, config:{...}}`
 * was speculative and crashed the dashboard with a TypeError on first
 * paint — see the 2026-05-28 incident.)
 */
export interface MySharesResponse {
  name: string;
  classAShares: number;
  classBShares: number;
  totalShares: number;
  classAPercent: number;
  classBPercent: number;
  fullyDilutedPercent: number;
  shareClass: string;
  classATotalShares: number;
  classBTotalShares: number;
}

export interface CompanyMetricsResponse {
  totalPublishers: number;
  installs: number;
  sessions: number;
  monthlyGrowth?: {
    publishers: number;
    installs: number;
    sessions: number;
  };
  lastUpdated?: string;
}

export interface MyTransactionsItem {
  _id: string;
  shareClass: 'A' | 'B';
  previousShares: number;
  newShares: number;
  change: number;
  reason?: string;
  createdAt: string;
}

export interface MyTransactionsResponse {
  docs: MyTransactionsItem[];
  totalDocs?: number;
  totalPages?: number;
  page?: number;
}

export interface MyDocument {
  _id: string;
  title: string;
  description?: string;
  fileUrl: string;
  fileName: string;
  createdAt: string;
}

export interface MyDocumentsResponse {
  docs: MyDocument[];
  totalDocs?: number;
}

export interface MyAnnouncement {
  _id: string;
  title: string;
  body: string;
  category: 'milestone' | 'product' | 'fundraising' | 'team' | 'general';
  pinned: boolean;
  publishedAt: string;
}

export interface MyAnnouncementsResponse {
  docs: MyAnnouncement[];
}

export interface MetricsTimeseriesPoint {
  month: string; // YYYY-MM
  publishers?: number;
  installs?: number;
  sessions?: number;
}

export interface MetricsTimeseriesResponse {
  series: MetricsTimeseriesPoint[];
}

export interface NetworkStatsResponse {
  reach?: {
    countries?: number;
    devices?: number;
  };
  financials?: {
    totalPaidOut?: number;
    activePayouts?: number;
  };
}

// ─── Fetchers ─────────────────────────────────────────────────────────────

const get = async <T>(path: string, params?: Record<string, unknown>): Promise<T> => {
  const r = await client.get<{ success?: boolean; data?: T } | T>(path, { params });
  // The shareholder-portal endpoints in nurev-api use the standard
  // { success, data } envelope from middleware/utils.handleSuccess.
  const body = r.data as { success?: boolean; data?: T };
  if (body && typeof body === 'object' && 'data' in body && body.data !== undefined) {
    return body.data;
  }
  return r.data as T;
};

export const fetchMyShares = () => get<MySharesResponse>('/shareholder-portal/my-shares');

export const fetchCompanyMetrics = () =>
  get<CompanyMetricsResponse>('/shareholder-portal/company-metrics');

export const fetchMyTransactions = (params: { limit?: number; page?: number } = {}) =>
  get<MyTransactionsResponse>('/shareholder-portal/my-transactions', params);

export const fetchMyDocuments = (params: { limit?: number; page?: number } = {}) =>
  get<MyDocumentsResponse>('/shareholder-portal/documents', params);

export const fetchMyAnnouncements = (params: { limit?: number } = {}) =>
  get<MyAnnouncementsResponse>('/shareholder-portal/announcements', params);

export const fetchMetricsTimeseries = (params: { months?: number } = {}) =>
  get<MetricsTimeseriesResponse>('/shareholder-portal/metrics-timeseries', params);

export const fetchNetworkStats = () =>
  get<NetworkStatsResponse>('/shareholder-portal/network-stats');
